/**
 * labelBadge — 라벨(꼬리표) 배지 한 벌. conceptBadge 의 라벨판이다.
 *
 * 같은 배지를 네 곳이 그린다: 문제 목록 표(ProblemHubV2/ProblemTable)·문제 목록 카드(ProblemSearchPanel)·
 * 문제 상세(ProblemViewTab)·시험지 조립 창의 검색 카드와 담은 문항(PaperComposeModal).
 * 2026-09-02 까지는 네 곳이 같은 클래스 문자열을 각자 들고 있었다 — 값이 같아도 한 곳을 고치면
 * 나머지가 뒤처지는 구조라 여기 한 벌만 둔다(오너 지시 2026-09-02 「한 부품으로」).
 *
 * 규격(오너 지시 2026-08-29 「목록 카드와 같은 규격」):
 *  - gray-100 알약 · 9px · **값 하나에 배지 하나**. 라벨은 여러 값이지 한 문장이 아니다 — 종전에 콤마로
 *    이어 붙인 한 줄을 truncate 로 잘랐을 때는 뒤쪽 값이 화면에 아예 없었다.
 *  - 배지 하나는 **160px(max-w-40)** 에서 자르고 title 로 전문을 준다. 긴 라벨(「2026학년도 SKY미래연
 *    정규 모의고사 17회」)이 줄을 다 먹는 것을 막는다(오너 지적). 상한이 칸의 100% 가 아니라 고정폭인
 *    이유는 표의 칼럼 폭이 내용에 맞춰 늘어나기 때문이다 — 실측 2026-08-29: `w-[13%]` 에 min-w-0·
 *    max-w-full·truncate 를 다 걸어도 라벨 칼럼이 199 → 279px 로 넓어지며 아무것도 잘리지 않았다.
 *  - 접지 않는다: wrap 으로 두면 다 보이고, 접었다 펴는 조작 자체가 없어진다.
 *
 * 줄(container)의 간격은 자리마다 다르다 — 표 칸은 여백 없이, 카드는 개념 줄과 mt-0.5 로 붙고, 상세는
 * 제목 있는 구역이라 gap-1.5 다. 그래서 **배지의 얼굴은 여기서 못박고, 줄의 클래스만 className 으로
 * 받는다.** 배지 클래스를 자리에서 덮어쓰는 길은 두지 않는다 — 그 길이 있으면 네 벌로 되돌아간다.
 */

/** 배지 하나의 얼굴 — 이 문자열이 유일한 정본이다. 검사(labelBadge.test · *.labels.test)가 이 값을 그대로 잰다. */
export const LABEL_BADGE_CLASS = 'max-w-40 truncate rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] text-gray-600';

/**
 * 라벨 값을 꼬리표 배열로. 서버가 두 모양으로 준다 — 목록의 `labels` 와 상세의 `labels_raw` 둘 다
 * 콤마로 이어진 한 줄이거나 문자열 배열이다. 배열 원소 안의 콤마도 가른다(종전 네 곳 모두 join 뒤 split
 * 이었다). 문자열이 아닌 원소는 버린다 — join 하면 "[object Object]" 가 배지로 서는 일이 있었다.
 */
export function splitLabels(value) {
  const parts = Array.isArray(value)
    ? value.filter((v) => typeof v === 'string')
    : [value == null ? '' : String(value)];
  return parts.flatMap((s) => s.split(',')).map((t) => t.trim()).filter(Boolean);
}

/** 배지 하나. 잘린 것은 title 로 전문을 준다. */
export function LabelBadge({ text }) {
  return <span title={text} className={LABEL_BADGE_CLASS}>{text}</span>;
}

/**
 * 라벨 한 벌. `labels` 는 서버 값 그대로(한 줄·배열 어느 쪽이든) 넣는다.
 * 값이 없으면 `empty` 를 돌려준다 — 기본 null 이라 줄 자체가 없다. 표 칸처럼 빈 자리를 표시해야 하는
 * 곳만 empty 에 「—」를 준다.
 */
export function LabelBadges({ labels, className = 'flex flex-wrap items-center gap-1', empty = null }) {
  const tags = splitLabels(labels);
  if (tags.length === 0) return empty;
  return (
    <div className={className}>
      {tags.map((t, i) => <LabelBadge key={`l-${i}`} text={t} />)}
    </div>
  );
}
